import React, { useEffect, useRef, useContext, useState } from "react";
import { Button, Table, Form, Input, InputNumber, Upload, message } from "antd";
import { useModel } from "../../../models";
import StockSelectionModal from "./StockSelectionModal";
import StockCheckService from "./StockCheckService";
import { YhAuth } from "../../../components/Auth";
import { ResourceAuthEnum } from "../../../utils/resourcesAuth";

const EditableContext = React.createContext();

const EditableRow = ({ index, ...props }) => {
    const [form] = Form.useForm();
    return (
        <Form form={form} component={false}>
            <EditableContext.Provider value={form}>
                <tr {...props} />
            </EditableContext.Provider>
        </Form>
    );
};
const EditableCell = ({
    title,
    editable,
    children,
    dataIndex,
    record,
    handleSave,
    ...restProps
}) => {
    const [editing, setEditing] = useState(false);
    const inputRef = useRef();
    const form = useContext(EditableContext);
    useEffect(() => {
        if (editing) {
            inputRef.current.focus();
        }
    }, [editing]);

    const toggleEdit = () => {
        setEditing(!editing);
        form.setFieldsValue({
            [dataIndex]: record[dataIndex],
        });
    };

    const save = async e => {
        try {
            const values = await form.validateFields();
            toggleEdit();
            handleSave({ ...record, ...values });
        } catch (errInfo) {
            console.log('Save failed:', errInfo);
        }
    };

    let childNode = children;
    if (editable) {
        childNode = editing ? (
            <>
                {
                    (() => {
                        if (dataIndex === "checkNum") {
                            return <Form.Item
                                style={{
                                    margin: 0,
                                }}
                                name={dataIndex}
                                rules={[
                                    {
                                        required: true,
                                        message: `${title}不能为空.`,
                                    },
                                ]}
                            >
                                <InputNumber
                                    ref={inputRef}
                                    onPressEnter={save}
                                    onBlur={save}
                                    style={{ width: "100%" }}
                                />
                            </Form.Item>
                        } else if (dataIndex === "scrapNum") {
                            return <Form.Item
                                style={{
                                    margin: 0,
                                }}
                                name={dataIndex}
                                rules={[
                                    {
                                        required: true,
                                        message: `${title}不能为空.`,
                                    },
                                    () => ({
                                        validator: (_, value) => {
                                            return (
                                                (value && parseInt(value) > parseInt(record["checkNum"])) ?
                                                    Promise.reject('报废数量不能大于盘点数量!') : Promise.resolve()
                                            )
                                        }
                                    }),
                                ]}
                            >
                                <InputNumber
                                    ref={inputRef}
                                    onPressEnter={save}
                                    onBlur={save}
                                    style={{ width: "100%" }}
                                />
                            </Form.Item>
                        } else if (dataIndex === "inventoryListDesc") {
                            return <Form.Item
                                style={{
                                    margin: 0,
                                }}
                                name={dataIndex}
                                rules={[
                                    {
                                        required: true,
                                        message: `${title}不能为空.`,
                                    },
                                ]}
                            >
                                <Input.TextArea
                                    ref={inputRef}
                                    onPressEnter={save}
                                    onBlur={save}
                                    style={{ width: "100%" }}
                                />
                            </Form.Item>
                        }
                    }
                    )()
                }
            </>
        ) : (
                <div
                    className="editable-cell-value-wrap"
                    style={{
                        paddingRight: 24,
                    }}
                    onClick={toggleEdit}
                >
                    {children}
                </div>
            );
    }

    return <td {...restProps}>{childNode}</td>;
};

const columns = [
    {
        title: '序号',
        width: 50,
        render: (_, __, index) => <span>{index + 1}</span>,
    },
    {
        title: '商品编号',
        width: 120,
        dataIndex: 'materialCode',
        align: 'left',
    },
    {
        title: '商品名称',
        width: 120,
        dataIndex: 'materialName',
    },
    {
        title: '图号',
        width: 120,
        dataIndex: 'replaceNum',
    },
    {
        title: '仓库',
        width: 160,
        dataIndex: 'warehouseName',
    },
    {
        title: '货位',
        width: 120,
        dataIndex: 'shelfCode',
    },
    {
        title: '账面库存量',
        width: 160,
        dataIndex: 'inventoryNum',
    },
    {
        title: '实物盘点量*',
        width: 160,
        dataIndex: 'checkNum',
        editable: true,
    },
    {
        title: '盘点结果',
        width: 80,
        dataIndex: 'checkResult',
        // render: text => parseInt(text) === 0 ? "相符" : parseInt(text) > 0 ? "盘盈" : "盘亏",
        render: (text, record) => {
            const { checkNum, inventoryNum } = record;
            const differenceNum = checkNum - inventoryNum;
            let ret = "";
            if (differenceNum === 0) {
                ret = "相符";
            } else if (differenceNum < 0) {
                ret = "盘亏";
            } else {
                ret = "盘盈";
            }
            return ret;
        }
    },
    {
        title: '差异量',
        width: 80,
        dataIndex: 'differenceNum',
    },
    {
        title: '差异金额',
        width: 80,
        dataIndex: 'differencePrice',
    },
    {
        title: '报废数量*',
        width: 80,
        dataIndex: 'scrapNum',
        editable: true,
    },
    {
        title: '报废金额',
        width: 80,
        dataIndex: 'scrapPrice',
    },
    {
        title: '差异原因*',
        dataIndex: 'inventoryListDesc',
        editable: true,
        render: text => text ? text : '空'
    },
];

const components = {
    body: {
        row: EditableRow,
        cell: EditableCell,
    },
};

const mapColumns = handleSave => columns.map(col => {
    if (!col.editable) {
        return col;
    }

    return {
        ...col,
        onCell: record => ({
            record,
            editable: col.editable,
            dataIndex: col.dataIndex,
            title: col.title,
            handleSave,
        }),
    };
});

export default function () {
    const [
        {
            checkDetailList,
            checkDetailListSelectedRowKeys,
            check,
            checkStatistic,
        },
        {
            setStockItemSelectModelVisible,
            setCheckDetailListSelectedRowKeys,
            removeCheckDetailList,
            setCheckDetailList,
            importCheckDetailList,
            exportDetail,
            setCheckStatistic,
            loadStockItem
        }
    ] = useModel("stockCheck");
    const { orderStatus } = check;

    useEffect(() => {
        let inventoryBook = 0,//账面库存量
            inventoryPhysical = 0,//实物盘点量
            differenceTotal = 0,//差异总量
            scrapTotal = 0,//报废总量
            differenceAmount = 0.00,
            scrapAmount = 0.00;
        checkDetailList.forEach(el => {
            const { inventoryNum = 0, checkNum = 0, differenceNum = 0, scrapNum = 0, differencePrice = 0, scrapPrice = 0 } = el;

            inventoryBook = inventoryBook + inventoryNum;
            inventoryPhysical = inventoryPhysical + checkNum;
            differenceTotal = differenceTotal + differenceNum;
            differenceAmount = differenceAmount + parseFloat(differencePrice);
            scrapTotal = scrapTotal + scrapNum;
            scrapAmount = scrapAmount + parseFloat(scrapPrice);

        });
        setCheckStatistic({
            inventoryBook,
            inventoryPhysical,
            differenceTotal,
            scrapTotal,
            differenceAmount,
            scrapAmount,
        })
    }, [checkDetailList]);

    const handleSave = row => {
        const { averagePrice, inventoryNum, checkNum } = row;

        row.differenceNum = checkNum - inventoryNum;
        if (row.differenceNum === 0) {
            row.checkResult = 0;
        } else if (row.differenceNum < 0) {
            row.checkResult = -1;
        } else {
            row.checkResult = 1;
        }
        //差异总额
        row.differencePrice = (row.differenceNum * parseFloat(averagePrice)).toFixed(2);
        //报废总额
        row.scrapPrice = (row.scrapNum * parseFloat(averagePrice)).toFixed(2);
        const mappedList = checkDetailList.map(el => el.key === row.key ? { ...el, ...row } : el);
        setCheckDetailList(mappedList);
    }
    const onAdd = () => {
        const { warehouseId } = check;
        if (!!!warehouseId) {
            message.error("请选择仓库!");
        } else {
            //加载数据
            const { warehouseId, shelvesId } = check;
            loadStockItem({ showMsg: false, data: { warehouseId, shelvesId, pageNum: 1, pageSize: 5 } });
            setStockItemSelectModelVisible(true);
        }
    }
    const onDelete = () => {
        removeCheckDetailList();
        setCheckDetailListSelectedRowKeys([]);
    }
    const onExport = () => {
        const { warehouseId, shelvesId } = check;
        if (warehouseId) {
            exportDetail({ showMsg: true, data: { warehouseId, shelvesId } });
        } else {
            message.error("请选择仓库");
        }

    }
    const onUploadSuccess = retData => {
        importCheckDetailList(retData);
    }

    const TableHeader = () => {
        return (
            <div style={{ margin: "0 0 8px", display: "flex" }}>
                <div className="detail-list-statistic  color-primary">
                    <span className="margin-left-8">
                        账面库存量：{checkStatistic ? checkStatistic.inventoryBook : 0}
                    </span>
                    <span className="margin-left-16">
                        实物盘点量：{checkStatistic ? checkStatistic.inventoryPhysical : 0}
                    </span>
                    <span
                        className="margin-left-16">
                        差异量：{checkStatistic ? checkStatistic.differenceTotal : 0}
                    </span>
                    <span
                        className="margin-left-16">
                        差异总额：{checkStatistic ? Number(checkStatistic.differenceAmount).toFixed(2) : 0.00}
                    </span>
                    <span
                        className="margin-left-16">
                        报废量：{checkStatistic ? checkStatistic.scrapTotal : 0}
                    </span>
                    <span
                        className="margin-left-16">
                        报废总额：{checkStatistic ? Number(checkStatistic.scrapAmount).toFixed(2) : 0.00}
                    </span>
                </div>
                <div
                    style={{ width: "460px", textAlign: "right" }}
                    hidden={
                        check.orderStatus === "031003" || check.orderStatus === "031002"
                    }
                >
                    <YhAuth resCode={ResourceAuthEnum.ST_STTAKE_ED_ADD_M}>
                        <Button type="primary" onClick={onAdd}>新增商品</Button>
                    </YhAuth>
                    <YhAuth resCode={ResourceAuthEnum.ST_STTAKE_ED_DEL_M}>
                        <Button className="margin-left-8" onClick={onDelete}>删除商品</Button>
                    </YhAuth>
                    <YhAuth resCode={ResourceAuthEnum.ST_STTAKE_ED_DLOAD}>
                        <Button className="margin-left-8" onClick={onExport}>导出盘库商品</Button>
                    </YhAuth>
                    <YhAuth resCode={ResourceAuthEnum.ST_STTAKE_ED_IM}>
                        <Upload {...StockCheckService.getImportDetailCfg({ onUploadSuccess })}>
                            <Button className="margin-left-8">导入</Button>
                        </Upload>
                    </YhAuth>
                </div>
            </div>
        )
    }
    return (
        <>
            <TableHeader />

            <Table
                components={components}
                columns={mapColumns(handleSave)}
                dataSource={checkDetailList}
                bordered
                pagination={false}
                rowSelection={{
                    type: "checkbox",
                    onChange: (selectedRowKeys) => {
                        setCheckDetailListSelectedRowKeys(selectedRowKeys)
                    },
                    selectedRowKeys: checkDetailListSelectedRowKeys
                }}
                scroll={{ x: 'max-content' }}
            />
            <StockSelectionModal />
        </>
    )
}